import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import hospitalInfo from "../constants/hospitalInfo";
import formatDate from "./formatDate";
import logoBase64 from "../constants/logoBase64";

const currency = (n) =>
    `Rs. ${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Downloads a formal monthly attendance and payroll slip PDF using the Green Naturopathy Bill Template design.
 */
export function downloadAttendancePDF(monthly, year, month) {
    if (!monthly || !monthly.staff) return;

    const staff = monthly.staff;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;

    // Palette matching the Bill Template
    const brandGreen = [75, 128, 38];      // #4b8026 Organic Leaf Green
    const grayHeaderBg = [209, 213, 219];  // #d1d5db Header gray
    const grayContentBg = [249, 250, 251]; // #f9fafb Soft card gray
    const grayBorder = [209, 213, 219];    // #d1d5db Box border
    const textDark = [31, 41, 55];         // #1f2937 Charcoal
    const textMuted = [107, 114, 128];     // #6b7280 Muted gray
    const statusGreen = [115, 167, 66];    // #73a742 Status green badge

    const monthName = new Date(year, month - 1, 1).toLocaleString("default", { month: "long" });

    // ---------- Top Header: Title on Left, Logo on Right ----------
    doc.setFont("times", "bold");
    doc.setFontSize(36);
    doc.setTextColor(...brandGreen);
    doc.text("Attendance Slip", marginX, 54);

    // Official Logo on top right (exact content size: 4.5cm x 2.26cm -> 127.56pt x 64.06pt)
    const logoWidth = 127.56;
    const logoHeight = 64.06;
    const logoX = pageWidth - marginX - logoWidth;
    const logoY = 18;
    if (logoBase64) {
        try {
            doc.addImage(logoBase64, "PNG", logoX, logoY, logoWidth, logoHeight);
        } catch (e) {
            console.warn("Could not render logo in Attendance PDF", e);
        }
    }

    let y = 92;

    // ---------- Side-by-side Gray-capped cards ----------
    const cardGap = 14;
    const cardWidth = (pageWidth - (marginX * 2) - cardGap) / 2;
    const cardHeight = 84;
    const headerHeight = 17;

    const baseSalary = staff.monthlySalary || 0;
    const presentDays = monthly.summary?.present || 0;
    const halfDays = monthly.summary?.halfDay || 0;
    const holidays = monthly.summary?.holiday || 0;
    const leavesTaken = monthly.summary?.absent || 0;
    const payableDays = monthly.payableDays || (30 - leavesTaken - (halfDays * 0.5));
    const deduction = monthly.deduction || 0;
    const payableSalary = monthly.payableSalary || Math.max(0, baseSalary - deduction);

    // LEFT CARD: Staff Details
    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(marginX, y, cardWidth, cardHeight, 3, 3, "FD");

    doc.setFillColor(...grayHeaderBg);
    doc.rect(marginX, y, cardWidth, headerHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.text("STAFF DETAILS", marginX + 10, y + 12);

    let leftTextY = y + 28;
    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    doc.text((staff.name || "-").toUpperCase(), marginX + 10, leftTextY);

    leftTextY += 13;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("STAFF ID:", marginX + 10, leftTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(staff.staffId || "-", marginX + 85, leftTextY);

    leftTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("DESIGNATION:", marginX + 10, leftTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(staff.designation || "Staff Member", marginX + 85, leftTextY);

    leftTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("PHONE:", marginX + 10, leftTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(staff.phone || "-", marginX + 85, leftTextY);

    // RIGHT CARD: Attendance & Payroll Summary
    const rightCardX = marginX + cardWidth + cardGap;
    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(rightCardX, y, cardWidth, cardHeight, 3, 3, "FD");

    doc.setFillColor(...grayHeaderBg);
    doc.rect(rightCardX, y, cardWidth, headerHeight, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.text("PAYROLL & ATTENDANCE SUMMARY", rightCardX + 10, y + 12);

    let rightTextY = y + 28;
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("PERIOD:", rightCardX + 10, rightTextY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(17, 24, 39);
    doc.text(`${monthName.toUpperCase()} ${year}`, rightCardX + 85, rightTextY);

    rightTextY += 13;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("PAID DAYS:", rightCardX + 10, rightTextY);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...textDark);
    doc.text(`${payableDays} / 30 Days`, rightCardX + 85, rightTextY);

    rightTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("NET SALARY:", rightCardX + 10, rightTextY);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...brandGreen);
    doc.text(currency(payableSalary), rightCardX + 85, rightTextY);

    rightTextY += 12;
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...textMuted);
    doc.text("STATUS:", rightCardX + 10, rightTextY);

    // Status Badge
    doc.setFillColor(...statusGreen);
    doc.roundedRect(rightCardX + 85, rightTextY - 8, 55, 11, 2, 2, "F");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("VERIFIED", rightCardX + 112.5, rightTextY, { align: "center" });

    y += cardHeight + 14;

    // ---------- Attendance Performance Box (Clean Grid) ----------
    autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        theme: "plain",
        styles: {
            fontSize: 8,
            cellPadding: 5,
            textColor: textDark,
            lineColor: grayBorder,
            lineWidth: 0.5
        },
        headStyles: {
            fillColor: [229, 231, 235], // #e5e7eb
            textColor: textDark,
            fontStyle: "bold",
            fontSize: 8
        },
        head: [["ATTENDANCE BREAKDOWN (30 DAYS STANDARD BASE)", "", "", ""]],
        body: [
            ["Base Monthly Salary:", currency(baseSalary), "Present Days:", `${presentDays} Days`],
            ["Paid Holidays / Off Days:", `${holidays} Days`, "Half Days (0.5 Cut):", `${halfDays} Days`],
            ["Leaves Taken (Absent):", `${leavesTaken} Days`, "Total Paid Days:", `${payableDays} / 30 Days`],
            ["Leave Deductions:", `- ${currency(deduction)}`, "NET PAYABLE SALARY:", currency(payableSalary)]
        ],
        columnStyles: {
            0: { fontStyle: "bold", cellWidth: 140, fillColor: grayContentBg },
            1: { cellWidth: 118 },
            2: { fontStyle: "bold", cellWidth: 140, fillColor: grayContentBg },
            3: { cellWidth: 117, fontStyle: "bold" }
        },
        didParseCell: (data) => {
            if (data.row.index === 3 && (data.column.index === 2 || data.column.index === 3)) {
                data.cell.styles.textColor = brandGreen;
                data.cell.styles.fontStyle = "bold";
            }
        }
    });

    y = doc.lastAutoTable.finalY + 14;

    // ---------- Daily Attendance Log Table ----------
    const tableRows = (monthly.attendance || []).map((a) => {
        const [yy, mm, dd] = (a.date || "").split("-").map(Number);
        const dayOfWeek = (yy && mm && dd) ? new Date(yy, mm - 1, dd).toLocaleDateString("en-US", { weekday: "long" }) : "-";
        return [
            formatDate(a.date),
            dayOfWeek,
            a.status || "-"
        ];
    });

    autoTable(doc, {
        startY: y,
        margin: { left: marginX, right: marginX },
        head: [["DATE", "DAY OF WEEK", "ATTENDANCE STATUS"]],
        body: tableRows.length > 0 ? tableRows : [["-", "-", "No attendance records recorded for this month."]],
        theme: "plain",
        styles: {
            fontSize: 8,
            cellPadding: 4,
            textColor: textDark,
            lineColor: grayBorder,
            lineWidth: 0.5
        },
        headStyles: {
            fillColor: [229, 231, 235], // #e5e7eb
            textColor: textDark,
            fontSize: 8,
            fontStyle: "bold"
        },
        alternateRowStyles: {
            fillColor: grayContentBg
        },
        columnStyles: {
            0: { cellWidth: 110 },
            1: { cellWidth: 160 },
            2: { fontStyle: "bold" }
        },
        didParseCell: (data) => {
            if (data.section === "body" && data.column.index === 2) {
                const val = data.cell.raw;
                if (val === "Present") data.cell.styles.textColor = [16, 185, 129];
                else if (val === "Holiday") data.cell.styles.textColor = [59, 130, 246];
                else if (val === "Half Day") data.cell.styles.textColor = [245, 158, 11];
                else if (val === "Absent") data.cell.styles.textColor = [239, 68, 68];
            }
        }
    });

    y = doc.lastAutoTable.finalY + 16;

    // Check if space is needed for signature
    if (y + 90 > pageHeight - 50) {
        doc.addPage();
        y = 50;
    }

    // Policy Note Box
    doc.setFillColor(...grayContentBg);
    doc.setDrawColor(...grayBorder);
    doc.roundedRect(marginX, y, pageWidth - (marginX * 2), 24, 3, 3, "FD");

    doc.setTextColor(...textMuted);
    doc.setFontSize(7);
    doc.setFont("helvetica", "normal");
    doc.text("Policy: Present = Full Day Paid. Half Day = 0.5 Day Deducted. Holiday = Paid Off Day. Absent = 1.0 Day Deducted. Calculated on 30-day standard monthly base.", marginX + 10, y + 15);
    y += 45;

    // Signatures
    doc.setDrawColor(...grayBorder);
    doc.line(marginX, y, marginX + 140, y);
    doc.line(pageWidth - marginX - 160, y, pageWidth - marginX, y);

    doc.setFontSize(8.5);
    doc.setTextColor(...textDark);
    doc.setFont("helvetica", "bold");
    doc.text("Staff Signature & Acknowledgment", marginX + 10, y + 14);
    doc.text("Authorized Signatory", pageWidth - marginX - 130, y + 14);

    // ---------- Bottom Hospital Address Banner (Identical to Bill Template) ----------
    const footerY = pageHeight - 38;
    doc.setDrawColor(...grayBorder);
    doc.setLineWidth(0.75);
    doc.line(marginX, footerY - 10, pageWidth - marginX, footerY - 10);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(55, 65, 81);
    const centreName = hospitalInfo.centreName || "GREEN NATUROPATHY";
    const addressLine = `${centreName}: ${(hospitalInfo.address || "194-C, SATHY ROAD, GANAPATHY, COIMBATORE")}, ${(hospitalInfo.city || "COIMBATORE")}, ${(hospitalInfo.state || "TAMIL NADU")} ${(hospitalInfo.pincode || "641006")}`;
    doc.text(addressLine.toUpperCase(), pageWidth / 2, footerY, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...textMuted);
    const phoneVal = hospitalInfo.phone ? `+91 ${hospitalInfo.phone}` : "+91 9345316319";
    const emailVal = hospitalInfo.email || "greennaturopathycbe@gmail.com";
    const webVal = hospitalInfo.websiteDisplay || "greennaturopathy.com";
    doc.text(`📞 ${phoneVal}   •   ✉️ ${emailVal}   •   🌐 ${webVal}`, pageWidth / 2, footerY + 10, { align: "center" });

    // Save PDF
    const safeStaffName = (staff.name || "Staff").replace(/[^a-zA-Z0-9]/g, "_");
    doc.save(`Attendance_Statement_${safeStaffName}_${monthName}_${year}.pdf`);
}
